import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import useExtendedState from '../../hooks/useExtendedState';
import TextField from '../fields/TextField';
import SaveButton from '../buttons/SaveButton';
import useMultiValidator from '../../hooks/useMultiValidator';
import {
    USER_ADDRESS_MAX_LENGTH,
    USER_EMAIL_MAX_LENGTH,
    USER_CODE_MAX_LENGTH,
    USER_ID_MAX_LENGTH,
    USER_NAME_MAX_LENGTH,
    USER_PHONE_MAX_LENGTH,
    userAddressValidator,
    userCodeValidator,
    userEmailValidator,
    userIdValidator,
    userNameValidator,
    userPhoneValidator,
} from '../../validators/users';
import CheckboxList from '../fields/CheckboxList';
import useExtendedMutation from '../../hooks/useExtendedMutation';
import { ALL_USERS_QUERY_NAME, CREATE_USER } from '../../graphql/users';
import { userPropertiesType } from '../../types';
import UserMemberAssignment, { extractRealAssignableMembers } from './UserMemberAssignment';
import { createZoneItems, extractMemberZones, syncAssignedZonesWithMembers } from '../../utils/UserZones';
import './_styles_/UserCreateTab.css';
import {
    createRoleItem,
    handleBrokerWithBidder,
} from '../../utils/UserRoles';
import ExpandedListTab from '../ExpandedListTab';
import { compositeValidator, wafValidator } from '../../hooks/validationSupport';

UserCreateTab.propTypes = {
    hideHandler: PropTypes.func.isRequired,
    userProps: userPropertiesType.isRequired,
};

function UserCreateTab(props) {
    const {
        hideHandler,
        userProps,
    } = props;
    const { t } = useTranslation();
    const {
        availableRoles,
        availableAssignedZones,
        assignableMembers,
        allowedRoleCombinations,
        clearingEnabled,
    } = userProps;
    const userIdState = useExtendedState('');
    const userCodeState = useExtendedState('');
    const nameState = useExtendedState('');
    const emailState = useExtendedState('');
    const phoneState = useExtendedState('');
    const addressState = useExtendedState('');
    const rolesState = useExtendedState([]);
    const assignedMembersState = useExtendedState([]);
    const assignedZonesState = useExtendedState([]);
    const [realAssignableMembers, setRealAssignableMembers] = useState(extractRealAssignableMembers(availableRoles, assignableMembers, []));
    const [isConflictingMember, setConflictingMember] = useState(false);
    const setNewAssignedMembers = (newAssignedMembers) => {
        const realAssignableMemberIds = realAssignableMembers.map((realAssignableMember) => realAssignableMember.memberId);
        const isConflicting = newAssignedMembers.some((assignedMember) => !realAssignableMemberIds.includes(assignedMember.memberId));
        setConflictingMember(isConflicting);
        assignedZonesState.setValue(syncAssignedZonesWithMembers(assignedZonesState.value, assignedMembersState.value, newAssignedMembers));
        assignedMembersState.setValue(newAssignedMembers);
    };
    const resetAssignedMembersAndZones = () => {
        assignedMembersState.reset();
        assignedZonesState.reset();
    };
    const setNewRoles = (newRoles) => {
        const newRolesToSet = handleBrokerWithBidder(rolesState.value, newRoles);
        rolesState.setValue(newRolesToSet);
        const newRealAssignableMembers = extractRealAssignableMembers(availableRoles, assignableMembers, newRolesToSet);
        const newRealAssignableMemberIds = newRealAssignableMembers.map((newRealAssignableMember) => newRealAssignableMember.memberId);
        const isConflicting = assignedMembersState.value.some((assignedMember) => !newRealAssignableMemberIds.includes(assignedMember.memberId));
        setConflictingMember(isConflicting);
        setRealAssignableMembers(newRealAssignableMembers);
    };
    const canAssignMembersToUser = rolesState.value.some((role) => {
        const foundAvailableRole = availableRoles.find((availableRole) => availableRole.role === role);
        return foundAvailableRole
            && foundAvailableRole.allowedMemberTypes
            && foundAvailableRole.allowedMemberTypes.length > 0;
    });
    const assignedZoneOptions = createZoneItems(extractMemberZones(assignedMembersState.value));
    // with less than two zones in the system there is nothing to choose from, so the zones are not offered
    // and the user is simply assigned all zones of their members
    const assignedZonesAreSelectable = availableAssignedZones != null;
    const assignedZonesToSubmit = assignedZonesAreSelectable
        ? assignedZonesState.value
        : assignedZoneOptions.map((assignedZoneOption) => assignedZoneOption.id);
    const { validators, isValid } = useMultiValidator(false, {
        userId: compositeValidator(userIdValidator(t), wafValidator(t)),
        userCode: compositeValidator(userCodeValidator(t), wafValidator(t)),
        name: compositeValidator(userNameValidator(t), wafValidator(t)),
        email: compositeValidator(userEmailValidator(t), wafValidator(t)),
        phone: userPhoneValidator(t),
        address: compositeValidator(userAddressValidator(t), wafValidator(t)),
    }, {
        phone: true,
        address: true,
        userCode: !clearingEnabled,
    });
    const [isDisabled, setIsDisabled] = useState(true);
    const initialValidity = isConflictingMember ? Promise.reject() : Promise.resolve();
    Promise.all([initialValidity, isValid()])
        .then(() => setIsDisabled(false))
        .catch(() => setIsDisabled(true));

    const extendedMutation = useExtendedMutation(CREATE_USER, {
        variables: {
            racf: userIdState.value,
            userCode: clearingEnabled ? userCodeState.value : null,
            name: nameState.value,
            email: emailState.value,
            phone: phoneState.value,
            address: addressState.value,
            roles: rolesState.value,
            memberIds: canAssignMembersToUser
                ? assignedMembersState.value.map((assignedMember) => assignedMember.memberId)
                : null,
            assignedZones: canAssignMembersToUser ? assignedZonesToSubmit : null,
        },
        successCallback: hideHandler,
        successMessageOptions: { key: 'users.notification.user-create-success', dataProperty: 'createUser' },
        failureMessageOptions: { key: 'users.notification.user-create-failure', params: { name: nameState.value, racf: userIdState.value } },
        refetchQueries: [ALL_USERS_QUERY_NAME],
    });
    const assignableRolesMap = availableRoles.filter((availableRole) => availableRole.isAssignable).map((assignableRole) => createRoleItem(t, assignableRole.role));
    return (
        <ExpandedListTab
            className="user-create-tab"
            title={t('users.user-detail')}
        >
            <div className="rich-list-row-expanded--fields">
                <TextField
                    inputId="user-create--user-id"
                    label={t('users.user-id')}
                    state={userIdState}
                    className="user-create-tab--fields--user-id"
                    maxLength={USER_ID_MAX_LENGTH}
                    validator={validators.userId}
                    placeholder={t('users.placeholder.user-id')}
                    isRequired
                />
                {clearingEnabled && (
                    <TextField
                        inputId="user-create--user-code"
                        label={t('users.user-code')}
                        state={userCodeState}
                        className="user-create-tab--fields--user-code"
                        maxLength={USER_CODE_MAX_LENGTH}
                        validator={validators.userCode}
                        placeholder={t('users.placeholder.user-code')}
                        isRequired
                    />
                )}
                <TextField
                    inputId="user-create--name"
                    label={t('users.name')}
                    state={nameState}
                    className="user-create-tab--fields--name wide"
                    maxLength={USER_NAME_MAX_LENGTH}
                    validator={validators.name}
                    placeholder={t('users.placeholder.name')}
                    isRequired
                />
                <TextField
                    inputId="user-create--email"
                    label={t('users.email')}
                    state={emailState}
                    className="user-create-tab--fields--email wide"
                    maxLength={USER_EMAIL_MAX_LENGTH}
                    validator={validators.email}
                    inputMode="email"
                    placeholder={t('users.placeholder.email')}
                    isRequired
                />
                <TextField
                    inputId="user-create--phone"
                    label={t('users.phone')}
                    state={phoneState}
                    className="user-create-tab--fields--phone"
                    maxLength={USER_PHONE_MAX_LENGTH}
                    validator={validators.phone}
                    inputMode="tel"
                    placeholder={t('users.placeholder.phone')}
                />
                <TextField
                    inputId="user-create--address"
                    label={t('users.address')}
                    state={addressState}
                    className="user-create-tab--fields--address x-wide"
                    maxLength={USER_ADDRESS_MAX_LENGTH}
                    validator={validators.address}
                    placeholder={t('users.placeholder.address')}
                />
            </div>
            <div className="user-create-tab--roles">
                <CheckboxList
                    label={t('users.roles')}
                    name="roles-list"
                    values={assignableRolesMap}
                    allowedCombinations={allowedRoleCombinations}
                    state={rolesState}
                    onChange={setNewRoles}
                    className="user-create-tab--fields--roles"
                />
            </div>
            {assignedZonesAreSelectable && assignedZoneOptions.length > 0 && canAssignMembersToUser && (
                <CheckboxList
                    label={t('users.user-assigned-zones')}
                    name="user-assigned-zones-list"
                    values={assignedZoneOptions}
                    state={assignedZonesState}
                    className="user-create-tab--fields--user-assigned-zones"
                    inputId="user-create--user-assigned-zones"
                />
            )}
            {canAssignMembersToUser && (
                <div className="user-create-tab--members">
                    <UserMemberAssignment
                        assignedMembers={assignedMembersState.value}
                        assignableMembers={realAssignableMembers}
                        onMemberAssignment={setNewAssignedMembers}
                        isChanged={assignedMembersState.isChanged}
                        onReset={resetAssignedMembersAndZones}
                    />
                </div>
            )}
            <div className="rich-list-row-expanded--buttons">
                <SaveButton extendedMutation={extendedMutation} disabled={isDisabled} confirmationMessage={t('users.confirm-create-user', { name: nameState.value, userId: userIdState.value })} />
            </div>
        </ExpandedListTab>
    );
}

export default UserCreateTab;
